import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useIsVendor } from "~/shared/hooks/use-is-vendor.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";
import { pickEndpoint } from "~/shared/services/api/pick-endpoint.ts";

import type { UpdateWebUserBody } from "../schemas/user-info-schema.ts";

// Some backends ack the update with a plain string body, others return
// 200/204 with no body (which `parseResponseBody` reports as `undefined`).
// Accept either so a successful save isn't rejected as a Zod failure.
const updateResponseSchema = z.union([z.string(), z.undefined()]);

export function useUpdateWebUser() {
	const queryClient = useQueryClient();
	const { isInternal } = useDeploymentType();
	const { isVendor } = useIsVendor();

	const url = pickEndpoint(
		{
			internal: apiRoutes.UPDATEWEBUSER,
			external: apiRoutes.UPDATEWEBUSER,
			vendor: apiRoutes.UPDATEVENDORWEBUSER,
		},
		{ isInternal, isVendor },
	);

	return useMutation({
		mutationFn: async (body: UpdateWebUserBody) => {
			const result = await postAndValidate(url, body, updateResponseSchema);
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ["currentUserInfo"] });
		},
	});
}
